import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Clapperboard, Sparkles, Loader2, Video, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useProject } from '@/context/ProjectContext';
import { toast } from 'sonner';
import ProviderBadge from '@/components/ProviderBadge';
import ReportButton from '@/components/ReportButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VideoReelPanel = () => {
  const { currentProject } = useProject();

  const [reels, setReels] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState(null); // pending VideoReel awaiting review/generation
  const [generating, setGenerating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const fetchReels = async () => {
    if (!currentProject) return;
    try {
      setFetching(true);
      const res = await axios.get(`${API}/projects/${currentProject.id}/video`);
      setReels(res.data.filter((r) => r.status === 'ready'));
    } catch (error) {
      console.error('Failed to fetch video reels:', error);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchReels();
  }, [currentProject?.id]);

  const handleSuggest = async () => {
    try {
      setDrafting(true);
      const res = await axios.post(`${API}/projects/${currentProject.id}/video/suggest-prompt`);
      setDraft(res.data);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to draft a reel concept');
    } finally {
      setDrafting(false);
    }
  };

  const handleGenerate = async () => {
    if (!draft) return;
    try {
      setGenerating(true);
      setShowConfirm(false);
      const res = await axios.post(`${API}/projects/${currentProject.id}/video/${draft.id}/generate`, {
        prompt: draft.prompt,
      });
      setReels((prev) => [res.data, ...prev]);
      setDraft(null);
      toast.success('Reel generated');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Video generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (reelId) => {
    try {
      await axios.delete(`${API}/projects/${currentProject.id}/video/${reelId}`);
      setReels((prev) => prev.filter((r) => r.id !== reelId));
    } catch (error) {
      toast.error('Failed to delete reel');
    }
  };

  if (!currentProject) return null;

  return (
    <div className="h-full overflow-auto p-6" data-testid="video-reel-panel">
      <div className="max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="font-heading text-2xl text-foreground mb-2">Video Reels</h2>
              <p className="text-muted-foreground">AI-generated short vertical video &mdash; real generation cost applies, see below</p>
            </div>
            <ReportButton section="social" projectId={currentProject.id} />
          </div>

          <div className="glass rounded-2xl p-6 border border-border mb-6">
            <div className="flex items-start gap-3 mb-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <AlertTriangle className="w-4 h-4 text-yellow-700 mt-0.5 shrink-0" />
              <p className="text-xs text-yellow-800">
                Unlike everything else in OMNICREATE, video generation costs real money per clip (~$0.25 for a 5-second reel via Fal.ai's Wan 2.5 model &mdash; no free tier exists for AI video anywhere yet). Concept drafting below is free; you'll always see the concept and confirm the cost before anything is actually generated.
              </p>
            </div>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Clapperboard className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-heading text-lg text-foreground">Concept</h3>
                <p className="text-sm text-muted-foreground">Drafted free from your Brand Kit &mdash; review and edit before generating</p>
              </div>
            </div>

            {!draft ? (
              <Button
                onClick={handleSuggest}
                disabled={drafting}
                className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50"
                data-testid="reel-suggest-btn"
              >
                {drafting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Drafting concept...</> : <><Sparkles className="w-4 h-4 mr-2" />Suggest a Reel Concept</>}
              </Button>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-foreground/80">Video prompt (single continuous shot)</Label>
                  <Textarea
                    value={draft.prompt}
                    onChange={(e) => setDraft({ ...draft, prompt: e.target.value })}
                    className="min-h-[80px] bg-black/[0.03] border-border text-foreground"
                    data-testid="reel-prompt-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground/80">Caption</Label>
                  <Textarea
                    value={draft.caption}
                    onChange={(e) => setDraft({ ...draft, caption: e.target.value })}
                    className="min-h-[60px] bg-black/[0.03] border-border text-foreground"
                    data-testid="reel-caption-input"
                  />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <ProviderBadge provider={draft.generated_by} />
                  {(draft.hashtags || []).map((h, i) => (
                    <span key={i} className="text-xs text-primary/80 font-mono">#{h}</span>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    onClick={() => setShowConfirm(true)}
                    disabled={generating || !draft.prompt.trim()}
                    className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50"
                    data-testid="reel-generate-btn"
                  >
                    {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating video... (can take a minute or two)</> : <><Video className="w-4 h-4 mr-2" />Generate Video (~${draft.cost_estimate_usd?.toFixed(2)})</>}
                  </Button>
                  <Button variant="ghost" onClick={() => setDraft(null)} disabled={generating} className="text-muted-foreground">
                    Discard
                  </Button>
                </div>
              </div>
            )}
          </div>

          {fetching && reels.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          ) : reels.length === 0 ? (
            <div className="glass rounded-2xl p-10 border border-border text-center">
              <Clapperboard className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">No reels generated yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
              {reels.map((reel) => (
                <div key={reel.id} className="flex flex-col items-center gap-2">
                  <div className="rounded-[1.5rem] p-2 bg-[#111318] shadow-lg" style={{ width: '150px' }}>
                    <div className="rounded-[1.1rem] overflow-hidden bg-black" style={{ aspectRatio: '9/16' }}>
                      <video src={reel.video_url} controls loop className="w-full h-full object-cover" data-testid={`reel-video-${reel.id}`} />
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground text-center line-clamp-2 max-w-[150px]">{reel.caption}</p>
                  <div className="flex items-center gap-2">
                    <ProviderBadge provider={reel.generated_by} />
                    <button onClick={() => handleDelete(reel.id)} className="text-muted-foreground hover:text-red-400" data-testid={`reel-delete-${reel.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="glass border-border bg-card text-foreground max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">Generate this video?</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              This calls a paid video-generation API and will cost approximately <strong>${draft?.cost_estimate_usd?.toFixed(2)}</strong>. This is real spend, not simulated &mdash; there's no free tier for AI video generation.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setShowConfirm(false)} className="flex-1 bg-black/[0.03] border-border text-foreground hover:bg-black/[0.06]">
              Cancel
            </Button>
            <Button onClick={handleGenerate} className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="reel-confirm-generate-btn">
              Yes, generate
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VideoReelPanel;
